import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import type {ReactNode} from 'react';

import styles from './RoadmapProgressBar.module.css';

export interface RoadmapProgressBarSegment {
  key: string;
  label: string;
  count: number;
  color: string;
  tooltip?: ReactNode;
}

const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

export interface RoadmapProgressBarProps {
  title: ReactNode;
  summary: ReactNode;
  segments: RoadmapProgressBarSegment[];
  total: number;
  value: number;
  ariaLabel: string;
  ariaValueText: string;
  testId?: string;
  segmentTestIdPrefix?: string;
}

export const RoadmapProgressBar = ({
  title,
  summary,
  segments,
  total,
  value,
  ariaLabel,
  ariaValueText,
  testId,
  segmentTestIdPrefix,
}: RoadmapProgressBarProps) => {
  const progressMax = Math.max(0, total);
  const progressValue = Math.min(Math.max(0, value), progressMax);
  const visibleSegments = progressMax > 0 ? segments.filter((segment) => segment.count > 0) : [];

  return (
    <div className={styles.card} data-testid={testId}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <Text isBold>{title}</Text>
          </div>
          <div className={styles.headerSummary}>
            <Text size="small" color="subtle">
              {summary}
            </Text>
          </div>
        </div>
        <div
          className={styles.track}
          role="progressbar"
          aria-label={ariaLabel}
          aria-valuemin={0}
          aria-valuemax={progressMax}
          aria-valuenow={progressValue}
          aria-valuetext={ariaValueText}
        >
          {visibleSegments.map((segment) => {
            const percent = formatPercent(segment.count / progressMax);
            return (
              <Tooltip
                key={segment.key}
                title={segment.tooltip ?? `${segment.label}: ${percent}`}
                placement="top"
              >
                <span
                  className={styles.segment}
                  style={{
                    backgroundColor: segment.color,
                    width: `${(segment.count / progressMax) * 100}%`,
                  }}
                  data-testid={
                    segmentTestIdPrefix ? `${segmentTestIdPrefix}-${segment.key}` : undefined
                  }
                  aria-hidden="true"
                />
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
};
